import type { PartialPick, SetDefined, Simplify, Writable } from '@eturi/ts-helpers'
import { pick } from '@eturi/util'
import mapValues from 'lodash/mapValues.js'
import type { LatLng, RawGeofenceLatLon } from './LatLng.js'
import { toLatLng } from './LatLng.js'

export type GeofenceTrigger = 'entry' | 'exit'
export type GeofenceType = 'circle'

export type RawGeofence = {
	readonly active: boolean
	readonly address: Maybe<string>
	readonly incomplete?: false
	readonly lat: number
	readonly lon: number
	readonly name: string // e.g. 'School'
	readonly radius: number // in meters
	readonly type: GeofenceType
	readonly ts: Maybe<number>
	readonly triggers: GeofenceTrigger[]
	readonly users: string[]
}

export type RawGeofenceIncomplete = Simplify<
	PartialPick<
		RawGeofence,
		'active' | 'address' | 'lat' | 'lon' | 'radius' | 'ts' | 'triggers' | 'type'
	> &
		Pick<RawGeofence, 'name' | 'users'> & { readonly incomplete: true }
>

export type RawGeofenceAll = RawGeofence | RawGeofenceIncomplete

/**
 * Raw geofences info object that we get from the server, mapping id -> RawGeofence
 * This is what is returned by calling GET /geofences
 *
 * @example
 * Below is an example of the model
 *
 * {
 *   "257a3be9-534c-4d34-b23e-a7cb2c4f6f00": {
 *     "active": false,
 *     "name": 'School',
 *     "lat": 22.34,
 *     "lon": -27.211,
 *     "radius": 100,
 *     "type": "circle",
 *     "ts": 1513210536992,
 *     "triggers": ['entry'],
 *     "users": ['ca6a2889-f138-47ba-a5c1-2001438e8551']
 *   }
 * }
 *
 * @see RawGeofence
 */
export type RawGeofenceInfoAll = {
	readonly [geofenceId: string]: RawGeofenceAll
}

/**
 * Decorated geofence object. Radius normalized to number and lat / lng instead of RawLatLon.
 */
export type Geofence = Simplify<
	Omit<SetDefined<RawGeofence>, 'lat' | 'lon' | 'ts'> & Pick<RawGeofence, 'ts'> & LatLng
>

export type GeofenceIncomplete = Simplify<
	Omit<RawGeofenceIncomplete, 'lat' | 'lon'> & Partial<LatLng>
>

export type GeofenceAll = Geofence | GeofenceIncomplete

/**
 * Raw geofence object we get from the server
 * @see GeofenceBase
 */
// export type RawGeofence = GeofenceBase & {
// 	readonly lat: number
// 	readonly lon: number
// }

////////////////////////////////
// DECORATED Geofence Objects //
////////////////////////////////

/**
 * Map of geofence id -> geofence
 * @see Geofence
 */
export type GeofenceInfo = {
	readonly [geofenceId: string]: Geofence
}

export type GeofenceInfoAll = {
	readonly [geofenceId: string]: Geofence | GeofenceIncomplete
}

export const toGeofenceLatLonRaw = ({ lat, lng }: LatLng): RawGeofenceLatLon => ({
	lat,
	lon: lng,
})

/** Changes decorated back to raw geofences */
export const mapGeofenceInfoToRaw = (geofencesInfo: GeofenceInfo): RawGeofenceInfoAll =>
	mapValues(geofencesInfo, (geofence: Geofence) => {
		const { lat, lon } = toGeofenceLatLonRaw(geofence)
		const rawGeofence = { ...geofence, lat, lon }
		delete (rawGeofence as any).lng

		return rawGeofence
	})

const mapRawToGeofence = (rawGeofence: RawGeofence): Geofence => ({
	...pickRaw(rawGeofence),
	...toLatLng(rawGeofence),
	address: rawGeofence.address || '',
	incomplete: false,
	name: rawGeofence.name || '',
})

const pickRaw = <T extends RawGeofenceAll>(rawGeofenceAll: T) =>
	pick(rawGeofenceAll, ['active', 'address', 'name', 'radius', 'ts', 'triggers', 'users', 'type'])

const mapRawToIncompleteGeofence = (rawGeofence: RawGeofenceIncomplete): GeofenceIncomplete => ({
	...pickRaw(rawGeofence),
	lat: rawGeofence.lat,
	lng: rawGeofence.lon,
	address: rawGeofence.address || '',
	incomplete: true,
	name: rawGeofence.name || '',
})

/** Maps raw geofences to decorated */
export function mapRawToGeofenceInfo(
	rawGeofencesInfo: RawGeofenceInfoAll,
	type: 'complete',
): GeofenceInfo
export function mapRawToGeofenceInfo(
	rawGeofencesInfo: RawGeofenceInfoAll,
	type: 'all',
): GeofenceInfoAll
export function mapRawToGeofenceInfo(rawGeofencesInfo: RawGeofenceInfoAll): GeofenceInfo
export function mapRawToGeofenceInfo(
	rawGeofencesInfo: RawGeofenceInfoAll,
	type: 'complete' | 'all' = 'complete',
): unknown {
	const geofencesInfo: Writable<GeofenceInfo | GeofenceInfoAll> = {}

	for (const key in rawGeofencesInfo) {
		const rawGeofence = rawGeofencesInfo[key]
		if (rawGeofence.incomplete) {
			if (type == 'all') {
				geofencesInfo[key] = mapRawToIncompleteGeofence(rawGeofence)
			}
		} else {
			geofencesInfo[key] = mapRawToGeofence(rawGeofence)
		}
	}

	return geofencesInfo
}

export const MAX_ACCOUNT_GEOFENCES = 100
export const MAX_USER_GEOFENCES = 15
